import React, { useState, useEffect } from "react";
import { CalendarIcon, Clock } from "lucide-react";
import { format, parse, isValid } from "date-fns";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { cn } from "../lib/utils";

/**
 * Shadcn-styled date + time picker used to replace native <input type="datetime-local"> inputs.
 *
 * Props:
 *  - value: ISO datetime string or null
 *  - onChange: (isoString | "") => void
 *  - label: optional field label
 *  - disabled: boolean
 *  - minDate, maxDate: optional Date bounds
 *  - showTime: boolean (default true — include the time input)
 *  - placeholder: string (displayed when no value)
 *  - testId: data-testid to attach to the trigger
 */
export const DateTimePicker = ({
  value,
  onChange,
  label,
  disabled = false,
  minDate,
  maxDate,
  showTime = true,
  dateOnly = false, // when true, emits "yyyy-mm-dd" strings and hides time input
  placeholder = "Pick a date",
  testId,
  className,
  required = false,
}) => {
  // Parse value: if dateOnly expect yyyy-mm-dd; otherwise ISO datetime
  const parsedDate = React.useMemo(() => {
    if (!value) return null;
    if (dateOnly) {
      // Avoid timezone shift: parse yyyy-mm-dd as local date
      const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) return new Date(parseInt(m[1]), parseInt(m[2]) - 1, parseInt(m[3]));
    }
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }, [value, dateOnly]);
  const isValidDate = !!parsedDate;
  const [timeText, setTimeText] = useState(isValidDate ? format(parsedDate, "HH:mm") : "");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (isValidDate && !dateOnly) {
      setTimeText(format(parsedDate, "HH:mm"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emit = (d) => {
    if (dateOnly) {
      const pad = (n) => String(n).padStart(2, "0");
      onChange(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`);
    } else {
      onChange(d.toISOString());
    }
  };

  const handleDateSelect = (day) => {
    if (!day) return;
    const next = new Date(day);
    if (dateOnly) {
      next.setHours(0, 0, 0, 0);
      emit(next);
      setOpen(false);
      return;
    }
    if (showTime && timeText) {
      const t = parse(timeText, "HH:mm", new Date());
      if (isValid(t)) {
        next.setHours(t.getHours(), t.getMinutes(), 0, 0);
      }
    } else if (!showTime) {
      next.setHours(0, 0, 0, 0);
    } else {
      const now = new Date();
      next.setHours(now.getHours(), now.getMinutes(), 0, 0);
      setTimeText(format(next, "HH:mm"));
    }
    emit(next);
    if (!showTime) setOpen(false);
  };

  const handleTimeChange = (e) => {
    const t = e.target.value;
    setTimeText(t);
    if (!isValidDate) return;
    const parts = t.split(":");
    if (parts.length !== 2) return;
    const h = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    if (Number.isNaN(h) || Number.isNaN(m)) return;
    const next = new Date(parsedDate);
    next.setHours(h, m, 0, 0);
    emit(next);
  };

  const displayText = isValidDate
    ? dateOnly
      ? format(parsedDate, "EEE d MMM yyyy")
      : showTime
        ? format(parsedDate, "EEE d MMM yyyy 'at' HH:mm")
        : format(parsedDate, "EEE d MMM yyyy")
    : placeholder;

  return (
    <div className={cn("space-y-1", className)}>
      {label && (
        <label className="text-xs font-bold uppercase tracking-wide text-slate-500">
          {label}{required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      <Popover open={open} onOpenChange={(v) => !disabled && setOpen(v)}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            data-testid={testId}
            className={cn(
              "w-full flex items-center gap-2 px-3 py-2 rounded-md border-2 border-slate-200 bg-white text-left text-sm",
              "hover:border-orange-300 focus:border-orange-500 focus:outline-none transition-colors",
              disabled && "opacity-50 cursor-not-allowed",
              !isValidDate && "text-slate-400"
            )}
          >
            <CalendarIcon className="w-4 h-4 text-orange-500 flex-shrink-0" />
            <span className="flex-1 truncate">{displayText}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 bg-white" align="start">
          <Calendar
            mode="single"
            selected={isValidDate ? parsedDate : undefined}
            onSelect={handleDateSelect}
            disabled={(d) => (minDate && d < minDate) || (maxDate && d > maxDate)}
            initialFocus
          />
          {showTime && !dateOnly && (
            <div className="border-t p-3 flex items-center gap-2">
              <Clock className="w-4 h-4 text-slate-500" />
              <input
                type="time"
                value={timeText}
                onChange={handleTimeChange}
                className="flex-1 px-2 py-1 rounded border text-sm"
                data-testid={testId ? `${testId}-time` : undefined}
              />
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-xs font-bold uppercase text-orange-600 hover:text-orange-700"
              >
                Done
              </button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
};

export const DatePicker = (props) => <DateTimePicker {...props} dateOnly showTime={false} />;

export default DateTimePicker;
